// Imperative toast API backed by a tiny Zustand store. Callers don't
// need a hook — any module can import `toast` and fire a message:
//
//   toast.success('Section archived');
//   toast.error('Sync failed — try again');
//   toast.action({ title: 'Task deleted', actionLabel: 'undo', onAction: restore });
//
// The <Toaster /> component (rendered once at the root) subscribes to
// the store and renders the active toasts via Radix Toast primitives.

import { create } from 'zustand';

export type ToastVariant = 'success' | 'error' | 'info';

export interface ToastItem {
  id: string;
  variant: ToastVariant;
  title: string;
  description?: string;
  actionLabel?: string;
  onAction?: () => void;
  /** Total time on screen in ms. Defaults to 4500. */
  duration?: number;
}

interface ToastStore {
  toasts: ToastItem[];
  push: (toast: Omit<ToastItem, 'id'>) => string;
  dismiss: (id: string) => void;
}

const useToastStore = create<ToastStore>((set) => ({
  toasts: [],
  push: (input) => {
    const id =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
    set((s) => ({ toasts: [...s.toasts, { ...input, id }] }));
    return id;
  },
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

export const useToasts = () => useToastStore((s) => s.toasts);

type PushOptions = Omit<ToastItem, 'id' | 'variant' | 'title'>;

export const toast = {
  success(title: string, options?: PushOptions): string {
    return useToastStore.getState().push({ ...options, title, variant: 'success' });
  },
  error(title: string, options?: PushOptions): string {
    return useToastStore.getState().push({ ...options, title, variant: 'error' });
  },
  info(title: string, options?: PushOptions): string {
    return useToastStore.getState().push({ ...options, title, variant: 'info' });
  },
  /**
   * Convenience for toasts that include an actionable button (e.g. undo).
   * Defaults to the success variant + a slightly longer duration so the
   * user has time to react before the toast dismisses.
   */
  action(input: {
    title: string;
    actionLabel: string;
    onAction: () => void;
    variant?: ToastVariant;
    description?: string;
    duration?: number;
  }): string {
    const { variant = 'success', duration = 6000, ...rest } = input;
    return useToastStore.getState().push({ ...rest, variant, duration });
  },
  dismiss(id: string): void {
    useToastStore.getState().dismiss(id);
  },
};
